/**
 * Codebase Coherence Check — MCP Integration Module.
 * BET-99 / FEAT-182 / ARCH-19 / TEN-2781 / Lane D S1.
 *
 * Orchestrates the coherence pipeline: resolve registries from LOCAL source files
 * (the Convex server has no access to the agent's checkout — this stays connector-
 * side), call the SERVED kernel verdict (`chain.evaluateCoherence`,
 * kernel/codebaseCoherence/engine.ts), render markdown for orient/start and the
 * review gate, and produce a snapshot for persistence.
 *
 * Was: canonical engine synced from top-level src/lib/coherence/ (byte-hash pinned by
 * scripts/check-mcp-lib-drift.mjs). Now: this module keeps only what is genuinely
 * LOCAL — the filesystem resolver (resolver.ts), git-diff detection
 * (git-detection.ts), and ALL rendering. The pure algorithm moved to
 * kernel/codebaseCoherence/{engine.ts,types.ts} (STD-K1 clean) and is served over the
 * AKI gateway — so the coherence engine's sync script
 * (scripts/sync-coherence-to-mcp.mjs) and its top-level canonical copy are deleted, and
 * COHERENCE's ENTRY in the MCP drift gate is removed (TEN-2781, Lane D S1).
 *
 * Precise scope (do NOT overstate as "the drift gate retired"): only coherence's pin was
 * dropped. scripts/check-mcp-lib-drift.mjs is still live and still content-hash-pins
 * fieldTypes, workflowRun, and inferSourceDate (see that script's header), and the
 * separate embedded-copies drift check (scripts/generate-mcp-embedded-copies.mjs --check)
 * still pins flags.ts / logo-markup.ts. Those blocks and their sync scripts remain.
 *
 * ASYNC (MAJOR — not a drop-in): the engine call is now `await kernelQuery(...)`, so
 * the whole pipeline is async. `runCoherenceCheck`, `checkAndRender`, and
 * `renderCoherenceGate` all return Promises. Every call site awaits:
 * packages/mcp-server/src/tools/{start_pb.ts:270, orient.ts:796, orient.ts:963,
 * wrapup.ts:421}, and orient-shared.ts's `buildCoherenceSection` wrapper. Rendering
 * LOGIC is unchanged — but the call SHAPE is not, so this is not "rendering
 * unchanged" outright.
 *
 * VISIBLE FAIL-OPEN (TEN-2781 Design, MAJOR). On a `kernelQuery` rejection (gateway
 * unreachable), this degrades to an EXPLICIT skipped-state — never a silent pass that
 * reads as coherent/green:
 *   - Orient/start (`checkAndRender`): `## Codebase Coherence` + a single
 *     "_Coherence check skipped — gateway unreachable..._" line. No "All registries
 *     aligned", no gap counts.
 *   - Review gate (`renderCoherenceGate`): `### Coherence Gate` +
 *     `**Gate: SKIPPED** — ... This is NOT a pass; ...` and verdict `"unavailable"`
 *     — distinct from `"no-baseline"` (too few LOCAL registries resolved — never even
 *     reaches the gateway) and from any pass/degraded verdict.
 * This is fail-OPEN (the entry/orient/session still completes) but never SILENT (the
 * reader always sees that drift was not evaluated this session).
 */

import { REGISTRY_MANIFEST, COLLECTION_REGISTRY_RULES } from "./data.js";
import { resolveRegistries } from "./resolver.js";
import { kernelQuery, KernelCallError } from "../../client.js";
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import type { CoherenceReport, CoherenceSnapshot, CoherenceViolation, RuleSeverity } from "./types.js";

/**
 * Describe a `kernelQuery` rejection concretely — PR #405 review (Codex P2): every
 * failure used to collapse into the same "gateway unreachable" line, masking
 * deployment skew, an expired credential, a server-side validation rejection, or a
 * handler exception behind one undifferentiated message. `KernelCallError` (thrown by
 * client.ts's callGateway when the gateway RESPONDS with a non-success status —
 * validation/auth/handler failures) carries a real `.code`/`.message`; a plain network
 * failure (DNS, timeout, connection refused — callGateway's fetch itself throwing)
 * carries only `.message`. Either way this is still fail-OPEN (the caller always gets a
 * `'skipped'` outcome, never a thrown error) — just honest about WHY, per the doneWhen's
 * "never silent" requirement extending to "never opaque."
 */
function describeGatewayFailure(err: unknown): string {
  if (err instanceof KernelCallError) {
    return err.code ? `${err.code}: ${err.message}` : err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export type { CoherenceReport, CoherenceSnapshot, CoherenceViolation } from "./types.js";
export { detectTouchedRegistries, hasRegistryChanges } from "./git-detection.js";

type EvaluateCoherenceResult = GatewayRouteReturnByName<"chain.evaluateCoherence">;

/**
 * Outcome of one coherence-check attempt.
 * - `ok`: the served verdict was computed successfully.
 * - `no-data`: fewer than 2 registries resolved LOCALLY — nothing to compare. This is
 *   unrelated to gateway health; the served route is never even called (DEC-169: early
 *   exit before the round trip).
 * - `skipped`: the `chain.evaluateCoherence` gateway call rejected — VISIBLE fail-open,
 *   see file header. Never silently treated as "aligned." `reason` carries the concrete
 *   failure (network error, or the gateway's own code+message) — never opaque.
 */
export type CoherenceCheckOutcome =
  | { status: "ok"; report: CoherenceReport; snapshot: CoherenceSnapshot }
  | { status: "no-data" }
  | { status: "skipped"; reason: string };

/**
 * Run the full coherence check pipeline: resolve registries LOCALLY, then call the
 * served kernel verdict. Returns null-equivalent (`'no-data'`) when too few registries
 * resolve — same early-exit DEC-169 always did, now made explicit in the outcome type
 * instead of a bare `null` so callers can tell "nothing to check" apart from "gateway
 * unreachable."
 */
export async function runCoherenceCheck(projectRoot: string): Promise<CoherenceCheckOutcome> {
  const resolved = resolveRegistries(projectRoot, REGISTRY_MANIFEST);

  // Need at least 2 resolved registries for any rule to evaluate — a purely LOCAL
  // determination, made before ever touching the gateway (DEC-169 perf budget).
  if (Object.keys(resolved).length < 2) return { status: "no-data" };

  const resolvedWire = Object.entries(resolved).map(([id, slugs]) => ({ id, slugs: [...slugs] }));

  try {
    const result = await kernelQuery<EvaluateCoherenceResult>("chain.evaluateCoherence", {
      manifest: REGISTRY_MANIFEST,
      rules: COLLECTION_REGISTRY_RULES,
      resolved: resolvedWire,
    });
    return { status: "ok", report: result.report, snapshot: result.snapshot };
  } catch (err) {
    const reason = describeGatewayFailure(err);
    if (process.env.DEBUG_COHERENCE) {
      console.debug("[coherence] chain.evaluateCoherence gateway call failed:", reason);
    }
    return { status: "skipped", reason };
  }
}

// ── Orient / Start rendering ──────────────────────────────────────────────────

export function renderCoherenceLines(report: CoherenceReport): string[] {
  const lines: string[] = ["## Codebase Coherence"];

  if (report.summary.total === 0) {
    lines.push("All registries aligned — no gaps detected.");
    lines.push("");
    return lines;
  }

  const parts: string[] = [];
  if (report.summary.errors > 0) parts.push(`${report.summary.errors} error${report.summary.errors === 1 ? "" : "s"}`);
  if (report.summary.warnings > 0) parts.push(`${report.summary.warnings} warning${report.summary.warnings === 1 ? "" : "s"}`);
  if (report.summary.infos > 0) parts.push(`${report.summary.infos} info`);
  lines.push(`**${report.summary.total} gap${report.summary.total === 1 ? "" : "s"}** across ${report.totalRegistries} registries (${parts.join(", ")})`);

  const errors = report.violations.filter((v) => v.severity === "error");
  const warnings = report.violations.filter((v) => v.severity === "warning");

  if (errors.length > 0) {
    lines.push("");
    lines.push("**Errors** (must fix):");
    for (const v of errors.slice(0, 5)) {
      lines.push(`- ${v.message}`);
    }
    if (errors.length > 5) lines.push(`- ...and ${errors.length - 5} more`);
  }

  if (warnings.length > 0) {
    lines.push("");
    lines.push("**Warnings**:");
    for (const v of warnings.slice(0, 3)) {
      lines.push(`- ${v.message}`);
    }
    if (warnings.length > 3) lines.push(`- ...and ${warnings.length - 3} more`);
  }

  lines.push("");
  return lines;
}

/**
 * One-call convenience: check + render + snapshot.
 * Returns `null` ONLY for the LOCAL 'no-data' case (too few registries resolved —
 * same as pre-Lane-D behavior: no section rendered at all). A gateway-unreachable
 * `'skipped'` outcome is NOT null — it renders the visible skip banner (file header:
 * never silent).
 */
export async function checkAndRender(projectRoot: string): Promise<{
  lines: string[];
  snapshot: CoherenceSnapshot | null;
} | null> {
  const outcome = await runCoherenceCheck(projectRoot);
  if (outcome.status === "no-data") return null;

  if (outcome.status === "skipped") {
    return {
      lines: [
        "## Codebase Coherence",
        `_Coherence check skipped — gateway unreachable. Registry drift not evaluated this session. (Reason: ${outcome.reason})_`,
        "",
      ],
      snapshot: null,
    };
  }

  return {
    lines: renderCoherenceLines(outcome.report),
    snapshot: outcome.snapshot,
  };
}

// ── Delta rendering (FEAT-183 / FEAT-184) ────────────────────────────────────

export type CoherenceVerdict = "improved" | "degraded" | "unchanged";

export interface CoherenceDelta {
  before: CoherenceSnapshot;
  after: CoherenceSnapshot;
  gapsFixed: number;
  gapsIntroduced: number;
  netChange: number;
  verdict: CoherenceVerdict;
}

/**
 * Compare two snapshots to produce a delta with verdict.
 * Used by review workflow (FEAT-183) and session wrapup (FEAT-184).
 *
 * Note: `gapsFixed`/`gapsIntroduced` are derived from topGaps (capped at 10),
 * while `netChange` uses totalGaps (uncapped). When many gaps exist, the
 * itemized counts are a lower bound — the render copy reflects this.
 */
export function computeDelta(before: CoherenceSnapshot, after: CoherenceSnapshot): CoherenceDelta {
  const beforeSet = new Set(before.topGaps.map((g) => `${g.registry}::${g.slug}`));
  const afterSet = new Set(after.topGaps.map((g) => `${g.registry}::${g.slug}`));

  let gapsFixed = 0;
  for (const key of beforeSet) {
    if (!afterSet.has(key)) gapsFixed++;
  }
  let gapsIntroduced = 0;
  for (const key of afterSet) {
    if (!beforeSet.has(key)) gapsIntroduced++;
  }

  const netChange = after.totalGaps - before.totalGaps;
  const verdict: CoherenceVerdict =
    netChange < 0 ? "improved" : netChange > 0 ? "degraded" : "unchanged";

  return { before, after, gapsFixed, gapsIntroduced, netChange, verdict };
}

/**
 * Render a coherence delta as markdown lines for review or wrapup.
 * Gate logic: degraded → warning flag, improved → celebration, unchanged → neutral.
 */
export function renderCoherenceDelta(delta: CoherenceDelta): string[] {
  const lines: string[] = ["### Coherence Delta"];

  const p = (n: number) => n === 1 ? "" : "s";
  const absNet = Math.abs(delta.netChange);
  const approx = absNet > delta.gapsFixed + delta.gapsIntroduced;
  const prefix = approx ? "at least " : "";

  if (delta.verdict === "improved") {
    lines.push(
      `Registry coherence **improved**: ${prefix}${delta.gapsFixed} gap${p(delta.gapsFixed)} fixed` +
      (delta.gapsIntroduced > 0 ? `, ${delta.gapsIntroduced} introduced` : "") +
      ` (net ${delta.netChange}). ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} remain${delta.after.totalGaps === 1 ? "s" : ""}.`,
    );
  } else if (delta.verdict === "degraded") {
    lines.push(
      `**Registry coherence degraded**: ${prefix}${delta.gapsIntroduced} new gap${p(delta.gapsIntroduced)} introduced` +
      (delta.gapsFixed > 0 ? `, ${delta.gapsFixed} fixed` : "") +
      ` (net +${delta.netChange}). ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} total.`,
    );
  } else {
    lines.push(
      `Registry coherence unchanged. ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} remain${delta.after.totalGaps === 1 ? "s" : ""}` +
      (delta.gapsFixed > 0 ? ` (${delta.gapsFixed} fixed, ${delta.gapsIntroduced} introduced)` : "") +
      ".",
    );
  }

  lines.push("");
  return lines;
}

/**
 * Coherence gate for review workflow Round 02 (FEAT-183).
 * Produces a pass/fail section comparing session-start snapshot against current state.
 * Gracefully degrades to absolute report when no prior snapshot exists — and to an
 * explicit `"unavailable"` verdict (never a silent pass) when the gateway is unreachable.
 */
export async function renderCoherenceGate(
  projectRoot: string,
  sessionSnapshot: CoherenceSnapshot | null,
): Promise<{
  lines: string[];
  verdict: CoherenceVerdict | "no-baseline" | "unavailable";
  afterSnapshot: CoherenceSnapshot | null;
}> {
  const outcome = await runCoherenceCheck(projectRoot);

  if (outcome.status === "skipped") {
    return {
      lines: [
        "### Coherence Gate",
        `**Gate: SKIPPED** — coherence check could not run (gateway unreachable). This is NOT a pass; registry drift was not evaluated. Reason: ${outcome.reason}.`,
        "",
      ],
      verdict: "unavailable",
      afterSnapshot: null,
    };
  }

  if (outcome.status === "no-data") {
    // PR #405 review (Copilot): this branch means fewer than 2 registries resolved
    // LOCALLY (resolveRegistries's own early exit, see runCoherenceCheck) — it has
    // nothing to do with filesystem availability (a fully-readable filesystem with a
    // manifest that resolves too few registries hits this path too). The old wording
    // ("filesystem unavailable") implied an I/O failure that may not have occurred.
    return {
      lines: ["### Coherence Gate", "Could not run coherence check (fewer than 2 registries resolved locally).", ""],
      verdict: "no-baseline",
      afterSnapshot: null,
    };
  }

  const { report, snapshot: afterSnapshot } = outcome;

  if (!sessionSnapshot) {
    const absolute = renderCoherenceLines(report);
    absolute[0] = "### Coherence Gate (no baseline)";
    absolute.splice(1, 0, "_No session-start snapshot available — showing absolute state._");
    return { lines: absolute, verdict: "no-baseline", afterSnapshot };
  }

  const delta = computeDelta(sessionSnapshot, afterSnapshot);
  const lines = renderCoherenceDelta(delta);

  if (delta.verdict === "degraded") {
    lines.push("**Gate: WARN** — This implementation introduced registry drift. Review the new gaps before shipping.");

    const newGaps = delta.after.topGaps.filter(
      (g) => !delta.before.topGaps.some((b) => b.registry === g.registry && b.slug === g.slug),
    );
    if (newGaps.length > 0) {
      lines.push("");
      lines.push("New gaps:");
      for (const g of newGaps.slice(0, 5)) {
        lines.push(`- \`${g.slug}\` missing from \`${g.registry}\` (${g.severity})`);
      }
      if (newGaps.length > 5) lines.push(`- ...and ${newGaps.length - 5} more`);
    }
    lines.push("");
  } else if (delta.verdict === "improved") {
    lines.push("**Gate: PASS** — Nice work, coherence improved this session.");
    lines.push("");
  } else {
    lines.push("**Gate: PASS** — No coherence change.");
    lines.push("");
  }

  return { lines, verdict: delta.verdict, afterSnapshot };
}

// ── Persistent gap detection + tension offers (BET-101 / Phase 6) ────────────

export interface PersistentGap {
  registry: string;
  slug: string;
  severity: RuleSeverity;
  violation: CoherenceViolation | null;
}

/**
 * Find gaps that persisted across the session (present in both before and after).
 * These are candidates for Chain capture as tensions — the session didn't fix them,
 * so they represent structural drift worth tracking.
 */
export function findPersistentGaps(
  delta: CoherenceDelta,
  currentViolations: CoherenceViolation[],
): PersistentGap[] {
  const beforeSet = new Set(delta.before.topGaps.map((g) => `${g.registry}::${g.slug}`));
  const afterSet = new Set(delta.after.topGaps.map((g) => `${g.registry}::${g.slug}`));

  const persistentKeys = new Set<string>();
  for (const key of beforeSet) {
    if (afterSet.has(key)) persistentKeys.add(key);
  }

  if (persistentKeys.size === 0) return [];

  const violationMap = new Map<string, CoherenceViolation>();
  for (const v of currentViolations) {
    violationMap.set(`${v.registryId}::${v.collectionSlug}`, v);
  }

  return [...persistentKeys].map((key) => {
    const [registry = "", slug = ""] = key.split("::", 2);
    return {
      registry,
      slug,
      severity: (delta.after.topGaps.find((g) => g.registry === registry && g.slug === slug)?.severity ?? "warning") as RuleSeverity,
      violation: violationMap.get(key) ?? null,
    };
  });
}

/**
 * Render offer-to-capture section for persistent gaps.
 * Lists each gap with a ready-to-use `capture` command template.
 * BET-101 no-go #3: never auto-create — this is an offer, not an action.
 */
export function renderPersistentGapOffers(gaps: PersistentGap[]): string[] {
  if (gaps.length === 0) return [];

  const lines: string[] = [
    "### Persistent Coherence Gaps",
    "",
    "These gaps existed before and after this session — they represent structural registry drift.",
    "Want me to capture them as tensions on the Chain?",
    "",
  ];

  const SEVERITY_RANK: Record<RuleSeverity, number> = { error: 0, warning: 1, info: 2 };
  const errorsFirst = [...gaps].sort((a, b) =>
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
  );

  for (const gap of errorsFirst.slice(0, 5)) {
    const v = gap.violation;
    const fixHint = v?.fix ? ` — ${v.fix}` : "";
    lines.push(
      `- **\`${gap.slug}\`** missing from \`${gap.registry}\` (${gap.severity})${fixHint}`,
    );
  }

  if (errorsFirst.length > 5) {
    lines.push(`- ...and ${errorsFirst.length - 5} more`);
  }

  lines.push("");
  lines.push(
    '_Say "capture these as tensions" to accept them onto the Chain, or skip to continue._',
  );
  lines.push("");

  return lines;
}
